import { PageContent } from 'restaurantAdmin/layout/PageContent';
import { PageHeader } from 'restaurantAdmin/layout/PageHeader';
import { PricingPageNavLinks } from './PricingPageNavLinks';
import { Button, ButtonVariants } from '@components/button/Button';
import { useAdminFloorPlans } from 'restaurantAdmin/hooks/useAdminFloorPlans';
import {
  FloorPlan,
  type FloorPlanTablesRenderer,
} from '@components/floorPlan/FloorPlan';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { TableRating } from '@shared/types/floorPlans';
import { type FloorPlanTableUpdate, updateFloorPlanTables } from './apiHelpers';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { errorToast, successToast } from '@components/toasts/Toasts';
import { FloorPlanSelector } from '@components/floorPlan/FloorPlanSelector';
import { RatingsLegend } from './RatingsLegend';
import { Icon } from '@components/icon/Icon';
import cx from 'classnames';
import { Tooltip } from 'react-tooltip';
import styles from './TableRatingPage.scss';
import { FloorPlanTableWithRating } from './FloorPlanTableWithRating';

export const TableRatingPage = () => {
  const queryClient = useQueryClient();
  const { isLoading, selectedFloorPlan, floorPlans, setSelectedFloorPlanId } =
    useAdminFloorPlans();
  const { id: restaurantId } = useRestaurant();
  const [legendOpen, setLegendOpen] = useState(true);
  const [floorPlanTableUpdates, setFloorPlanTableUpdates] = useState<
    FloorPlanTableUpdate[]
  >([]);

  if (isLoading) {
    return null;
  }

  const onRatingChange = (id: string, rating: TableRating) => {
    const tableRatingUpdate = { id, rating };
    const updatesForOtherIds = floorPlanTableUpdates.filter(
      (update) => update.id !== id,
    );
    setFloorPlanTableUpdates([...updatesForOtherIds, tableRatingUpdate]);
  };

  const handleTableRatingsSave = async () => {
    try {
      await updateFloorPlanTables(restaurantId, floorPlanTableUpdates);
      await queryClient.invalidateQueries({
        queryKey: ['floorPlans', restaurantId],
      });
      successToast({ message: 'Pricing successfully updated!' });
      setFloorPlanTableUpdates([]);
    } catch (error) {
      errorToast({
        message: 'An error occurred saving table ratings. Please try again.',
      });
    }
  };

  /*
  Switching floor plans unmounts the current floor plan + tables and loses
  the table ratings stored in component state. When we switch back to the
  original floor plan, we can rely on the saved floor plan table updates
  state to retrieve the user's altered table ratings.
  */
  const renderedFloorPlanTables = selectedFloorPlan.floorPlanTables.map(
    (floorPlanTable) => ({
      ...floorPlanTable,
      rating:
        floorPlanTableUpdates.find((update) => update.id === floorPlanTable.id)
          ?.rating || floorPlanTable.rating,
    }),
  );

  const floorPlanTablesRenderer: FloorPlanTablesRenderer = (tableIconScale) => (
    <>
      {renderedFloorPlanTables.map((floorPlanTable) => (
        <FloorPlanTableWithRating
          key={floorPlanTable.id}
          id={floorPlanTable.id}
          rating={floorPlanTable.rating}
          iconName={floorPlanTable.iconName}
          iconWidthScale={floorPlanTable.iconWidthScale}
          onRatingChange={onRatingChange}
          left={floorPlanTable.left}
          name={floorPlanTable.name}
          orientation={floorPlanTable.orientation}
          tableIconScale={tableIconScale}
          top={floorPlanTable.top}
        />
      ))}
    </>
  );

  return (
    <>
      <PageHeader title="Pricing" category="Operations">
        <PricingPageNavLinks
          userHasUnsavedChanges={floorPlanTableUpdates.length > 0}
          saveChanges={handleTableRatingsSave}
          modalMessage={
            <p>
              You have unsaved changes to your table ratings. Please save your
              work before navigating to a different page.
            </p>
          }
        />
        <Button
          label="Save and Apply"
          onClick={handleTableRatingsSave}
          variant={ButtonVariants.Primary}
        />
      </PageHeader>
      <PageContent
        className={cx(
          styles.pageContentWithLegendWrapper,
          legendOpen && styles.pageContentWithLegend,
        )}
      >
        <div>
          <div className={styles.floorPlanTableNumber}>
            <FloorPlanSelector
              floorPlans={floorPlans}
              selectedFloorPlan={selectedFloorPlan}
              onChange={(selectedFloorPlanId) => {
                setSelectedFloorPlanId(selectedFloorPlanId);
              }}
            />
          </div>
          <div className={styles.floorPlanArea}>
            <div
              className={cx(
                styles.floorPlanWrapper,
                legendOpen && styles.floorPlanWrapperLegendOpen,
              )}
            >
              <FloorPlan
                backgroundSrc={selectedFloorPlan.backgroundSrc}
                floorPlanTablesRenderer={floorPlanTablesRenderer}
              />
            </div>
            {legendOpen && <RatingsLegend />}
            <Tooltip
              id="legend-toggle"
              place="bottom"
              content={legendOpen ? 'Hide Ratings' : 'Show Ratings'}
            />
            <button
              type="button"
              className={styles.legendToggle}
              data-tooltip-id="legend-toggle"
              onClick={() => setLegendOpen((open) => !open)}
              aria-label={
                legendOpen ? 'Close ratings legend' : 'Open ratings legend'
              }
              aria-expanded={legendOpen}
            >
              <Icon
                name="chevronRight"
                className={cx(
                  styles.legendToggleIcon,
                  !legendOpen && styles.legendToggleIconRotated,
                )}
              />
            </button>
          </div>
        </div>
      </PageContent>
    </>
  );
};
